import { TableHeadCell } from '../HeadCell';
import styles from './styles.module.scss';

/**
 * Table ヘッダー列の定義型。
 *
 * `TableHeadCell` の Props に加えて `key` を optional で受け取る。
 * `label` が他の列と衝突する可能性がある場合（例: カスタムフィールド列と固定列で同じラベルが
 * 重複するケース）に、明示的な React key を指定してキー衝突を防ぐ。
 *
 * `key` は React の予約済み属性のため `{...header}` のスプレッドで `TableHeadCell` には届かず、
 * `TableHeadCell` 側の Props 型変更は不要。
 */
type TableHeader = React.ComponentProps<typeof TableHeadCell> & {
  /**
   * 明示的な React key。
   *
   * 省略した場合は `label`（type='none' のときは `none-{index}`）をフォールバックとして使用する。
   * 動的データ由来のラベルを持つ列（カスタムフィールド等）では、ID ベースのユニークな値を
   * 指定してラベル衝突による React reconciliation の誤動作を防ぐこと。
   */
  key?: string;
};

type TableHeadersProps = TableHeader[];

type Props = {
  /** ヘッダー定義の配列 */
  headers: TableHeadersProps;
  /** テーブルボディの children（<tbody> 内に配置される） */
  children: React.ReactNode;
  /** true にするとカラム幅を固定できる（table-layout: fixed）。デフォルト: false */
  isFixed?: boolean;
  /** thead の <tr> に追加するクラス名（行単位の色・文字スタイル等の上書き用） */
  headRowClassName?: string;
  /** true にすると、テーブル幅が親を超えたときに Table 内側で横スクロールする。デフォルト: false */
  scrollable?: boolean;
  /** true にすると、縦スクロール時にヘッダー行を固定する。maxHeight と組み合わせて使用する。デフォルト: false */
  fixedHeader?: boolean;
  /** fixedHeader が true のとき、テーブルの最大高さを指定する（例: "720px", "90vh"）。デフォルト: 未指定 */
  maxHeight?: React.CSSProperties['maxHeight'];
  /** true にすると、テーブル自身の高さをビューポートの残り高さに合わせて可変にする。fixedHeader + maxHeight の代替であり、併用しない。デフォルト: false */
  fillHeight?: boolean;
};

export const Table: React.FC<Props> = ({
  headers,
  children,
  isFixed = false,
  headRowClassName,
  scrollable = false,
  fixedHeader = false,
  maxHeight,
  fillHeight = false,
}) => (
  <div
    className={[
      styles.table,
      scrollable ? styles.scrollable : undefined,
      fixedHeader || fillHeight ? styles.fixedHeader : undefined,
      fillHeight ? styles.fillHeight : undefined,
    ]
      .filter(Boolean)
      .join(' ')}
    style={!fillHeight && fixedHeader && maxHeight ? { maxHeight } : undefined}
  >
    <table className={isFixed ? styles.tableFixed : undefined}>
      <thead>
        <tr className={[styles.headRow, headRowClassName].filter(Boolean).join(' ')}>
          {headers.map(({ key, ...header }, index) => (
            <TableHeadCell
              key={key ?? (header.type === 'none' ? `none-${index}` : header.label)}
              {...header}
            />
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);
